import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, Mail, Lock, User as UserIcon, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Tx } from "../context/AutoTranslateContext";

const Register = () => {
  const { register, loginGoogle } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const r = await register(email, password, name || undefined);
    setBusy(false);
    if (r.ok) nav("/"); else setError(r.error);
  };

  return (
    <div data-testid="register-page" className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-gold/25 p-8 shadow-sm">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-full bg-forest/10 text-forest items-center justify-center mb-3">
            <UserPlus className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-3xl text-forest font-bold mb-2">
            <Tx>Create your account</Tx>
          </h1>
          <p className="text-sm text-mute"><Tx>Save your progress, protocols and Ruqyah bookings.</Tx></p>
        </div>

        <button
          data-testid="register-google-btn"
          onClick={loginGoogle}
          className="w-full mb-5 flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-forest/15 text-forest bg-white hover:bg-cream transition"
        >
          <span className="text-sm font-medium"><Tx>Continue with Google</Tx></span>
        </button>

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-forest/10" /></div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-xs uppercase tracking-widest text-mute"><Tx>or</Tx></span>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute"><Tx>Full name (optional)</Tx></span>
            <div className="mt-1.5 relative">
              <UserIcon className="w-4 h-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="register-name" type="text" autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-cream border border-forest/10 text-ink outline-none focus:border-forest transition"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute"><Tx>Email</Tx></span>
            <div className="mt-1.5 relative">
              <Mail className="w-4 h-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="register-email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-cream border border-forest/10 text-ink outline-none focus:border-forest transition"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute"><Tx>Password (min 8 characters)</Tx></span>
            <div className="mt-1.5 relative">
              <Lock className="w-4 h-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="register-password" type="password" autoComplete="new-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-cream border border-forest/10 text-ink outline-none focus:border-forest transition"
              />
            </div>
          </label>

          {error && (
            <div data-testid="register-error" className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            data-testid="register-submit"
            type="submit" disabled={busy}
            className="w-full py-3 rounded-full bg-forest text-sand hover:bg-sage transition font-medium disabled:opacity-60"
          >
            {busy ? <Tx>Creating account…</Tx> : <Tx>Create account</Tx>}
          </button>
        </form>

        <p className="mt-5 text-center text-xs text-mute">
          <Tx>Already have an account?</Tx>{" "}
          <Link data-testid="link-login" to="/login" className="text-forest font-medium hover:underline"><Tx>Sign in</Tx></Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
